import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiCalendar, FiRefreshCw, FiChevronLeft, FiChevronRight, FiFileText, FiCheckCircle, FiClock, FiX, FiPlus, FiDownload } from "react-icons/fi";
import "./staffcss/medicalappointments.css";
import CancelAppointmentModal from "../../components/usercomponents/CancelAppointmentModal";
import { appointmentService } from "../../services/appointmentService";
import { auth, db } from "../../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import Banner from "./Banner";
import {
  generateDynamicSlots,
  getServiceRule,
  formatDateToPH,
  getSlotValidation,
  SERVICE_FILTER_OPTIONS,
  matchesServiceFilter
} from "../../utils/appointmentUtils";
import {
  getAppointmentDateTimeValue,
  getAppointmentSortIndicator,
  getAppointmentSortTitle,
  getNextAppointmentSortConfig,
  sortAppointments
} from "../../utils/appointmentSorting";
import { isWeekend, getNextBusinessDay } from "../../utils/dateValidation";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import Portal from "../../components/Portal";

const getAssistanceBadgeColor = (reason) => {
  const colors = {
    minor: '#dbeafe',
    senior: '#fef3c7',
    pwd: '#d1fae5',
    nrnw: '#fce7f3',
    other: '#e5e7eb'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};

const getAssistanceBadgeTextColor = (reason) => {
  const colors = {
    minor: '#1e40af',
    senior: '#92400e',
    pwd: '#065f46',
    nrnw: '#9d174d',
    other: '#374151'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};

// Helper to get current PH Time components
function getPHNow() {
  const now = new Date();
  const phTimeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric',
    hour12: false
  });
  
  const parts = phTimeFormatter.formatToParts(now);
  return {
    year: parseInt(parts.find(p => p.type === 'year').value),
    month: parseInt(parts.find(p => p.type === 'month').value) - 1,
    day: parseInt(parts.find(p => p.type === 'day').value),
    hour: parseInt(parts.find(p => p.type === 'hour').value),
    minute: parseInt(parts.find(p => p.type === 'minute').value)
  };
}

const getStatusLabel = (status) => {
  const mapping = {
    'PENDING': 'Pending Approval',
    'CONFIRMED': 'Confirmed',
    'ACCEPTED': 'Accepted',
    'CANCELLING': 'User Requesting Cancellation',
    'COMPLETED': 'Completed',
    'CANCELLED_BY_USER': 'Cancelled by User',
    'CANCELLED_BY_PRO': 'Cancelled by You'
  };
  return mapping[status] || status;
};

const STATUS_FILTER_TABS = [
  { label: "All Statuses", value: "" },
  { label: "Pending", value: "PENDING" }
];

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting }) {
  const [selectedFormat, setSelectedFormat] = React.useState(null);

  React.useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const ACTION_ICONS = {
  approve: "https://www.svgrepo.com/show/422506/approve-hand-like.svg",
  reschedule: "https://www.svgrepo.com/show/440790/schedule-repeat-child.svg",
  cancel: "https://www.svgrepo.com/show/410435/cancel.svg"
};

const ReviewAppointments = ({
  appointments,
  setAppointments,
  onApprove,
  refreshAppointments,
  staffProfile,
  setShowWalkinModal,
  sidebarCollapsed
}) => {
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(null);
  const itemsPerPage = 10;

  const [viewing, setViewing] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Reschedule state
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [selectedRescheduleDate, setSelectedRescheduleDate] = useState(getNextBusinessDay(new Date()));
  const [selectedRescheduleTime, setSelectedRescheduleTime] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("schedule_conflict");
  const [rescheduleReasonOther, setRescheduleReasonOther] = useState("");
  const [rescheduleReasonError, setRescheduleReasonError] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isSubmittingReschedule, setIsSubmittingReschedule] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("info");

  // Cancel state
  const [cancelTarget, setCancelTarget] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [appointmentsWithAssistance, setAppointmentsWithAssistance] = useState([]);

  // Dynamic Time Slots
  const [timeSlots, setTimeSlots] = useState({ morning: [], afternoon: [] });

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Cache for booked slots by date to avoid repeated fetches
  const bookedSlotsCache = useRef({});

  useEffect(() => {
    if (rescheduleTarget) {
      const slots = generateDynamicSlots(rescheduleTarget.serviceId || rescheduleTarget.serviceName);
      setTimeSlots(slots);
    }
  }, [rescheduleTarget]);

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!rescheduleTarget || !selectedRescheduleDate) return;
      
      const dateStr = formatDateToPH(selectedRescheduleDate);
      
      // Check cache first
      if (bookedSlotsCache.current[dateStr]) {
        setBookedSlots(bookedSlotsCache.current[dateStr]);
        return;
      }
      
      try {
        const slots = await appointmentService.getBookedSlots(dateStr);
        // Cache the result
        bookedSlotsCache.current[dateStr] = slots;
        setBookedSlots(slots);
      } catch (err) {
        console.error("Error fetching booked slots:", err);
      }
    };
    fetchBookedSlots();
  }, [selectedRescheduleDate, rescheduleTarget]);

  // Ensure selectedRescheduleDate is never a weekend (safety net)
  useEffect(() => {
    if (rescheduleTarget && isWeekend(selectedRescheduleDate)) {
      const nextBusinessDay = getNextBusinessDay(selectedRescheduleDate);
      setSelectedRescheduleDate(nextBusinessDay);
      setBannerMessage("The clinic is closed on weekends. Automatically selected the next available business day.");
      setBannerType("error");
    }
  }, [selectedRescheduleDate, rescheduleTarget]);

  const isTimeSlotValid = (time) => {
    if (!rescheduleTarget) return { valid: false };
    
    // Prevent time slot selection on weekends
    if (isWeekend(selectedRescheduleDate)) {
      return { valid: false, reason: 'weekend' };
    }
    
    const rule = getServiceRule(rescheduleTarget.serviceId || rescheduleTarget.serviceName);
    const bookingType = rescheduleTarget.bookingType || (rescheduleTarget.type === "walk-in" ? "WALK_IN" : "ONLINE");

    const conflict = bookedSlots.some(slot => {
      if (slot.time !== time) return false;
      
      const sameService = slot.serviceId === rescheduleTarget.serviceId || slot.serviceName === rescheduleTarget.serviceName;
      if (!sameService) return false;

      if (rule && rule.separateBookingType === false) {
        return true; // 1 TOTAL
      } else {
        return slot.bookingType === bookingType; // 1 ONLINE + 1 WALK_IN
      }
    });

    if (conflict) return { valid: false, reason: 'Already booked' };

    const slotValidation = getSlotValidation(time, rescheduleTarget.serviceId || rescheduleTarget.serviceName);
    if (!slotValidation.valid) {
      return { valid: false, reason: slotValidation.reason === 'lunch' ? 'lunch' : 'service' };
    }

    const phNow = getPHNow();
    const [timeStr, period] = time.split(' ');
    let [hours, minutes] = timeStr.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const selectedDateStr = formatDateToPH(selectedRescheduleDate);
    const todayPHStr = formatDateToPH(new Date());

    const isToday = selectedDateStr === todayPHStr;
    const isPastDate = selectedDateStr < todayPHStr;

    if (isPastDate) return { valid: false, reason: "past" };
    if (isToday) {
      if (hours < phNow.hour) return { valid: false, reason: "past" };
      if (hours === phNow.hour && minutes <= phNow.minute) return { valid: false, reason: "past" };
    }
    return { valid: true };
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget || !selectedRescheduleDate || !selectedRescheduleTime) return;
    
    // Prevent weekend rescheduling
    if (isWeekend(selectedRescheduleDate)) {
      setBannerMessage("The clinic is closed on weekends. Please select a weekday for rescheduling.");
      setBannerType("error");
      return;
    }
    
    // Validate reschedule reason
    const finalReason = rescheduleReason === "other" ? rescheduleReasonOther : 
      rescheduleReason === "schedule_conflict" ? "Schedule conflict with another confirmed appointment" :
      rescheduleReason === "staff_unavailable" ? "Medical staff temporarily unavailable" :
      rescheduleReason === "schedule_passed" ? "Selected appointment schedule has already passed" :
      rescheduleReason === "clinic_adjustment" ? "Clinic operational adjustment" : rescheduleReason;
    
    if (!finalReason || !finalReason.trim()) {
      setRescheduleReasonError("Please provide a reason for rescheduling.");
      return;
    }

    const trimmedReason = finalReason.trim();
    if (trimmedReason.length < 10) {
      setRescheduleReasonError("Reason must be at least 10 characters long.");
      return;
    }
    if (trimmedReason.length > 500) {
      setRescheduleReasonError("Reason must not exceed 500 characters.");
      return;
    }
    
    setRescheduleReasonError("");
    setIsSubmittingReschedule(true);
    
    try {
      const dateStr = formatDateToPH(selectedRescheduleDate);
      await appointmentService.rescheduleAppointment(
        rescheduleTarget.id,
        dateStr,
        selectedRescheduleTime,
        trimmedReason,
        staffProfile
      );
      
      setBannerMessage("Appointment rescheduled and confirmed successfully!");
      setBannerType("success");
      setRescheduleTarget(null);
      setSelectedRescheduleTime("");
      setRescheduleReason("schedule_conflict");
      setRescheduleReasonOther("");
      
      // Refresh list if applicable
      if (typeof refreshAppointments === 'function') {
        refreshAppointments();
      }
    } catch (err) {
      setBannerMessage(err.message || "Failed to reschedule appointment.");
      setBannerType("error");
    } finally {
      setIsSubmittingReschedule(false);
    }
  };

  const handleCancel = async (apt, finalReason) => {
    if (!apt) return;

    setIsCancelling(true);
    try {
      await appointmentService.updateStatus(apt.id, 'CANCELLED_BY_PRO', {
        cancelReason: finalReason,
        cancelledByRole: 'the Medical Professional',
        cancelledByEmail: auth.currentUser?.email || ''
      });
      setAppointments(prev => prev.filter(a => a.id !== apt.id));
      setBannerMessage("Appointment cancelled successfully.");
      setBannerType("success");
      setCancelTarget(null);
    } catch (err) {
      setBannerMessage("Failed to cancel appointment.");
      setBannerType("error");
    } finally {
      setIsCancelling(false);
    }
  };

  const filteredAppointments = appointmentsWithAssistance.length > 0 ? appointmentsWithAssistance.filter((apt) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      (apt.patientName?.toLowerCase().includes(searchLower)) ||
      (apt.clinicName?.toLowerCase().includes(searchLower)) ||
      (apt.serviceName?.toLowerCase().includes(searchLower)) ||
      (apt.reference?.toLowerCase().includes(searchLower));

    const matchesService = matchesServiceFilter(apt, serviceFilter);
    const matchesStatus = statusFilter ? apt.status === statusFilter : true;

    let matchesDate = true;
    if (dateFrom || dateTo) {
      const aptDate = new Date(apt.date);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchesDate = matchesDate && aptDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        matchesDate = matchesDate && aptDate <= toDate;
      }
    }

    return matchesSearch && matchesService && matchesStatus && matchesDate;
  }) : appointments.filter((apt) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      (apt.patientName?.toLowerCase().includes(searchLower)) ||
      (apt.clinicName?.toLowerCase().includes(searchLower)) ||
      (apt.serviceName?.toLowerCase().includes(searchLower)) ||
      (apt.reference?.toLowerCase().includes(searchLower));

    const matchesService = matchesServiceFilter(apt, serviceFilter);
    const matchesStatus = statusFilter ? apt.status === statusFilter : true;

    let matchesDate = true;
    if (dateFrom || dateTo) {
      const aptDate = new Date(apt.date);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchesDate = matchesDate && aptDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        matchesDate = matchesDate && aptDate <= toDate;
      }
    }

    return matchesSearch && matchesService && matchesStatus && matchesDate;
  });

  const getStatusTabCount = (status) => {
    const searchLower = search.toLowerCase();
    const appointmentsToUse = appointmentsWithAssistance.length > 0 ? appointmentsWithAssistance : appointments;
    return appointmentsToUse.filter((apt) => {
      const matchesSearch =
        (apt.patientName?.toLowerCase().includes(searchLower)) ||
        (apt.clinicName?.toLowerCase().includes(searchLower)) ||
        (apt.serviceName?.toLowerCase().includes(searchLower)) ||
        (apt.reference?.toLowerCase().includes(searchLower));
      const matchesService = matchesServiceFilter(apt, serviceFilter);
      
      let matchesDate = true;
      if (dateFrom || dateTo) {
        const aptDate = new Date(apt.date);
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          matchesDate = matchesDate && aptDate >= fromDate;
        }
        if (dateTo) {
          const toDate = new Date(dateTo);
          matchesDate = matchesDate && aptDate <= toDate;
        }
      }
      
      const matchesStatus = status ? apt.status === status : true;

      return matchesSearch && matchesService && matchesDate && matchesStatus;
    }).length;
  };

  // Pagination helpers and filter handler
  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  // Set loading to false when appointments are loaded (even if empty)
  useEffect(() => {
    setLoading(false);
  }, [appointments]);

  const sortedAppointments = sortAppointments(filteredAppointments, sortConfig, (appointment, key) => {
    switch (key) {
      case "patientName":
        return appointment.patientName || null;
      case "reference":
        return appointment.reference || null;
      case "clinicName":
        return appointment.clinicName || null;
      case "serviceName":
        return appointment.serviceName || null;
      case "dateTime":
        return getAppointmentDateTimeValue(appointment);
      default:
        return null;
    }
  });

  const totalPages = Math.ceil(sortedAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = sortedAppointments.slice(startIndex, endIndex);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);

  const handleSort = (key) => {
    setSortConfig((current) => getNextAppointmentSortConfig(current, key));
    setCurrentPage(1);
  };

  const getSortHeaderProps = (key, label) => ({
    onClick: () => handleSort(key),
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSort(key);
      }
    },
    tabIndex: 0,
    title: getAppointmentSortTitle(label, key, sortConfig),
    "aria-label": getAppointmentSortTitle(label, key, sortConfig),
    "aria-sort": sortConfig?.key !== key
      ? "none"
      : sortConfig.direction === "asc" ? "ascending" : "descending",
    style: { cursor: "pointer" }
  });

  // Refresh appointments handler
  const handleRefreshAppointments = async () => {
    try {
      setIsRefreshing(true);
      if (typeof refreshAppointments === 'function') {
        await refreshAppointments();
      }
    } catch (err) {
      console.error('Error refreshing appointments', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let day = 1; day <= totalDays; day++) cells.push(day);
    return cells;
  };

  const exportToPDF = async () => {
    const pdf = createPDF({
      title: "Review Appointments Report"
    });

    let startY = pdf.contentStartY;

    // Add summary
    pdf.doc.setFontSize(10);
    pdf.doc.text(`Total Appointments: ${filteredAppointments.length}`, pdf.margin, startY);
    startY += 15;

    // Separate data by status
    const pendingItems = filteredAppointments.filter(item => item.status === 'PENDING');
    const cancellingItems = filteredAppointments.filter(item => item.status === 'CANCELLING');

    // Add pending items table if any
    if (pendingItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('PENDING APPROVAL', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time'];
      const rows = pendingItems.map(apt => [
        apt.patientName || 'N/A',
        apt.reference || 'N/A',
        apt.clinicName || 'N/A',
        apt.serviceName || 'N/A',
        `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add cancelling items table if any
    if (cancellingItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('USER REQUESTING CANCELLATION', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time'];
      const rows = cancellingItems.map(apt => [
        apt.patientName || 'N/A',
        apt.reference || 'N/A',
        apt.clinicName || 'N/A',
        apt.serviceName || 'N/A',
        `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
    }

    pdf.save(`review-appointments-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['REVIEW APPOINTMENTS REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      [],
      ['APPOINTMENTS SUMMARY'],
      ['Total Appointments', filteredAppointments.length],
      ['Pending', filteredAppointments.filter(item => item.status === 'PENDING').length],
      ['User Cancelling', filteredAppointments.filter(item => item.status === 'CANCELLING').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Separate data by status
    const pendingItems = filteredAppointments.filter(item => item.status === 'PENDING');
    const cancellingItems = filteredAppointments.filter(item => item.status === 'CANCELLING');

    const contentData = [
      ['APPOINTMENT DETAILS'],
    ];

    // Add pending items
    if (pendingItems.length > 0) {
      contentData.push(['PENDING APPROVAL']);
      contentData.push(['Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      pendingItems.forEach(apt => {
        contentData.push([
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    // Add cancelling items
    if (cancellingItems.length > 0) {
      contentData.push([]);
      contentData.push(['USER REQUESTING CANCELLATION']);
      contentData.push(['Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      cancellingItems.forEach(apt => {
        contentData.push([
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
    XLSX.utils.book_append_sheet(workbook, contentSheet, 'Appointments');

    XLSX.writeFile(workbook, `review-appointments-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async () => {
    let csvData = [
      ['REVIEW APPOINTMENTS REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      [],
      ['APPOINTMENTS SUMMARY'],
      ['Total Appointments', filteredAppointments.length],
      ['Pending', filteredAppointments.filter(item => item.status === 'PENDING').length],
      ['User Cancelling', filteredAppointments.filter(item => item.status === 'CANCELLING').length],
      [],
      ['APPOINTMENT DETAILS'],
    ];

    // Separate data by status
    const pendingItems = filteredAppointments.filter(item => item.status === 'PENDING');
    const cancellingItems = filteredAppointments.filter(item => item.status === 'CANCELLING');

    // Add pending items
    if (pendingItems.length > 0) {
      csvData.push(['PENDING APPROVAL']);
      csvData.push(['Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      pendingItems.forEach(apt => {
        csvData.push([
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    // Add cancelling items
    if (cancellingItems.length > 0) {
      csvData.push([]);
      csvData.push(['USER REQUESTING CANCELLATION']);
      csvData.push(['Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      cancellingItems.forEach(apt => {
        csvData.push([
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `review-appointments-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      setSelectedExportFormat(format);
      
      if (format === 'pdf') {
        await exportToPDF();
      } else if (format === 'excel') {
        await exportToExcel();
      } else if (format === 'csv') {
        await exportToCSV();
      }
    } catch (error) {
      console.error('Export error:', error);
      setBannerMessage('Export failed');
      setBannerType('error');
    } finally {
      setIsExporting(false);
      setSelectedExportFormat(null);
      setIsExportModalOpen(false);
    }
  };

  return (
    <div className="ma-main review-appointments-panel">
      <ExportModal 
        isOpen={isExportModalOpen} 
        onClose={() => setIsExportModalOpen(false)} 
        onExport={handleExport} 
        isExporting={isExporting} 
      />

      {/* Review Appointments Header */}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Medical Professional - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Medical Professional
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Review Appointments
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Review and manage pending appointment requests
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>
      
      {/* Review Appointments Sub Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px', 
        padding: '16px 0'
      }}>
        <div></div>
        <button 
          onClick={() => setShowWalkinModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(63, 98, 181, 0.3)',
            transition: 'all 0.2s'
          }}
        >
          <FiPlus />
          Create Appointment
        </button>
      </div>
      <Banner 
        message={bannerMessage} 
        type={bannerType} 
        onClose={() => setBannerMessage("")} 
      />

      {/* Custom Tooltip for Action Buttons */}
      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}
      {/* Top Row: Search, Date From, Date To, Service, Refresh */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="text"
            placeholder="Search Appointment"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              handleFilterChange();
            }}
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>

        <div style={{ position: 'relative', flex: '0 0 140px' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="date"
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              handleFilterChange();
            }}
          />
        </div>
        
        <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', flexShrink: 0 }}>to</div>
        
        <div style={{ position: 'relative', flex: '0 0 140px' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="date"
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              handleFilterChange();
            }}
          />
        </div>

        <select
          className="admin-aura-select"
          value={serviceFilter}
          onChange={(e) => {
            setServiceFilter(e.target.value);
            handleFilterChange();
          }}
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All Services</option>
          {SERVICE_FILTER_OPTIONS.map((service) => (
            <option key={service.value} value={service.value}>{service.label}</option>
          ))}
        </select>

        <button 
          onClick={handleRefreshAppointments}
          disabled={isRefreshing}
          className="admin-primary-gradient-btn"
          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
        >
          <FiRefreshCw className={isRefreshing ? "spinning" : ""} />
          Refresh
        </button>
        <button
          onClick={() => setIsExportModalOpen(true)}
          className="admin-primary-gradient-btn"
        >
          <FiFileText /> Export Report
        </button>
      </div>

      {/* Second Row: Status Buttons + Export to PDF + Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div className="floating-index-tabs ma-status-tabs" aria-label="Filter appointments by status" style={{ display: "flex", gap: "8px" }}>
          {STATUS_FILTER_TABS.map((tab) => (
            <button
              key={tab.value || "all"}
              type="button"
              className={`floating-tab ${statusFilter === tab.value ? "active floating-tab-active" : ""}`}
              onClick={() => {
                setStatusFilter(tab.value);
                handleFilterChange();
              }}
            >
              {tab.label}
              <span className="tab-count">{getStatusTabCount(tab.value)}</span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {filteredAppointments.length > 0 ? (
                `${startIndex + 1}-${Math.min(endIndex, filteredAppointments.length)} of ${filteredAppointments.length}`
              ) : (
                '0 of 0'
              )}
            </div>

            {/* Pagination Arrows */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button 
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                ←
              </button>
              
              <button 
                onClick={handleNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                →
              </button>
            </div>
        </div>
      </div>

      <div className="ma-table-wrap with-floating-tabs">
        <table className="ma-table">
          <thead>
          <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
            <th {...getSortHeaderProps("patientName", "Patient Name")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>Patient {getAppointmentSortIndicator(sortConfig, "patientName")}</th>
            <th {...getSortHeaderProps("reference", "Reference Number")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>Reference # {getAppointmentSortIndicator(sortConfig, "reference")}</th>
            <th {...getSortHeaderProps("clinicName", "Clinic")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>Clinic {getAppointmentSortIndicator(sortConfig, "clinicName")}</th>
            <th {...getSortHeaderProps("serviceName", "Service")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>Service {getAppointmentSortIndicator(sortConfig, "serviceName")}</th>
            <th {...getSortHeaderProps("dateTime", "Appointment Date & Time")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>Date & Time {getAppointmentSortIndicator(sortConfig, "dateTime")}</th>
            <th style={{ padding: '16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <>
              {[...Array(10)].map((_, i) => (
                <tr key={i} className="admin-aura-skeleton-row">
                  <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 3) * 15}%` }} /></td>
                  <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                  <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 4) * 10}%` }} /></td>
                  <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                  <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>
                  <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                </tr>
              ))}
            </>
          ) : paginatedAppointments.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                No appointments found
              </td>
            </tr>
          ) : (
            paginatedAppointments.map((apt) => (
            <tr key={apt.id}>
              <td>
                <div>{apt.patientName}</div>
                {apt.reasonForAssistance?.length > 0 && (
                  <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {apt.reasonForAssistance.map((reason, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '500',
                          textTransform: 'capitalize',
                          backgroundColor: getAssistanceBadgeColor(reason),
                          color: getAssistanceBadgeTextColor(reason)
                        }}
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                )}
              </td>
              <td>{apt.reference}</td>
              <td>{apt.clinicName}</td>
              <td>{apt.serviceName}</td>
              <td>
                <div>{apt.date}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{apt.time}</div>
              </td>
                <td style={{ textAlign: "center" }}>
                  <div className="ma-row-actions" style={{ justifyContent: "center" }}>
                    {apt.status === "PENDING" && (
                      <>
                        <button
                          type="button"
                          className="ma-action-btn approve"
                          aria-label="Approve Appointment"
                          onClick={() => onApprove && onApprove(apt)}
                          style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                          onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Approve Appointment", 'approve'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                        >
                          <FiCheckCircle size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn reschedule"
                          aria-label="Reschedule Appointment"
                          onClick={() => setRescheduleTarget(apt)}
                          style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Reschedule Appointment", 'info'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                        >
                          <FiClock size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn cancel"
                          aria-label="Cancel Appointment"
                          onClick={() => setCancelTarget(apt)}
                          style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                        >
                          <FiX size={16} />
                        </button>
                      </>
                    )}
                    {apt.status === "CANCELLING" && (
                      <button
                        type="button"
                        className="ma-action-btn cancel"
                        aria-label="Cancel Appointment"
                        onClick={() => {
                          setCancelTarget(apt);
                        }}
                        style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                        onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                      >
                        <FiX size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* VIEW MODAL */}
      {viewing && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card">
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => setViewing(null)}
            >
              ✕
            </button>
            <h3>Appointment Details</h3>
            <div className="ma-modal-details">
              <div className="ma-detail-row">
                <span className="ma-detail-label">Patient</span>
                <span className="ma-detail-value">{viewing.patientName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Reference #</span>
                <span className="ma-detail-value">{viewing.reference}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Clinic</span>
                <span className="ma-detail-value">{viewing.clinicName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Service</span>
                <span className="ma-detail-value">{viewing.serviceName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Date &amp; Time</span>
                <span className="ma-detail-value">
                  {viewing.date} • {viewing.time}
                </span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Status</span>
                <span className="ma-detail-value">
                  <span className={`ma-badge ma-badge-${viewing.status}`}>
                    {getStatusLabel(viewing.status)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* RESCHEDULE MODAL */}
      {rescheduleTarget && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card walkin-modal" style={{ maxWidth: '850px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '24px 32px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>Reschedule Appointment</h3>
            </div>
            
            <div className="modal-body-scroll" style={{ overflowY: 'auto', padding: '32px', flex: 1, backgroundColor: '#fff', minHeight: 0 }}>
              <p style={{ marginBottom: '24px', color: '#4b5563' }}>Select a new date and time for <strong>{rescheduleTarget.patientName}</strong>'s appointment.</p>
              
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                {/* Left Column: Current Appointment Info + Reason */}
                <div className="walkin-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Current Date</label>
                    <input type="text" value={rescheduleTarget.date} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Current Time</label>
                    <input type="text" value={rescheduleTarget.time} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Service</label>
                    <input type="text" value={rescheduleTarget.serviceName} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                    <div style={{ marginTop: '12px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#19458c', borderRadius: '50%' }} />
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Scheduled</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#fdebb2', borderRadius: '50%' }} />
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Walk-in</span>
                      </div>
                    </div>
                  </div>

                  {/* Reschedule Reason Field */}
                  <div className="form-group" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Reschedule Reason *</label>
                    <select 
                      value={rescheduleReason}
                      onChange={(e) => {
                        setRescheduleReason(e.target.value);
                        setRescheduleReasonError("");
                      }}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: '15px', cursor: 'pointer' }}
                    >
                      <option value="schedule_conflict">Schedule conflict with another confirmed appointment</option>
                      <option value="staff_unavailable">Medical staff temporarily unavailable</option>
                      <option value="schedule_passed">Selected appointment schedule has already passed</option>
                      <option value="clinic_adjustment">Clinic operational adjustment</option>
                      <option value="other">Other</option>
                    </select>
                    
                    {rescheduleReason === "other" && (
                      <textarea
                        value={rescheduleReasonOther}
                        onChange={(e) => {
                          setRescheduleReasonOther(e.target.value);
                          setRescheduleReasonError("");
                        }}
                        placeholder="Please specify the reason (10-500 characters)"
                        style={{
                          width: '100%',
                          marginTop: '12px',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: rescheduleReasonError ? '1px solid #ef4444' : '1px solid #e5e7eb',
                          backgroundColor: '#fff',
                          color: '#1f2937',
                          fontSize: '14px',
                          minHeight: '100px',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    )}
                    
                    {rescheduleReasonError && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#ef4444' }}>{rescheduleReasonError}</p>
                    )}
                  </div>
                </div>

                {/* Right Column: New Date & Time Selection */}
                <div className="walkin-right-col">
                  <div className="datetime-picker-card" style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                    <div className="calendar-widget">
                      <div className="calendar-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <button type="button" onClick={() => {
                          const d = new Date(selectedRescheduleDate);
                          d.setMonth(d.getMonth() - 1);
                          setSelectedRescheduleDate(d);
                        }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b', letterSpacing: '0.025em' }}>{selectedRescheduleDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</h4>
                        <button type="button" onClick={() => {
                          const d = new Date(selectedRescheduleDate);
                          d.setMonth(d.getMonth() + 1);
                          setSelectedRescheduleDate(d);
                        }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                      </div>

                      <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                          <div key={day} style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', padding: '8px 0' }}>{day}</div>
                        ))}
                        {getCalendarDays(selectedRescheduleDate).map((day, i) => {
                          if (!day) return <div key={`empty-${i}`} />;
                          const d = new Date(selectedRescheduleDate.getFullYear(), selectedRescheduleDate.getMonth(), day);
                          const isSelected = formatDateToPH(d) === formatDateToPH(selectedRescheduleDate);
                          const isToday = formatDateToPH(d) === formatDateToPH(new Date());
                          const isPast = formatDateToPH(d) < formatDateToPH(new Date());
                          const isWeekendDate = isWeekend(d);
                          const isDisabled = isPast || isWeekendDate;

                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                if (isWeekendDate) {
                                  setBannerMessage("The clinic is closed on weekends. Please select a weekday for rescheduling.");
                                  setBannerType("error");
                                  return;
                                }
                                if (!isPast) setSelectedRescheduleDate(d);
                              }}
                              disabled={isDisabled}
                              title={isWeekendDate ? "Clinic closed on weekends" : ""}
                              style={{
                                padding: '8px 0',
                                borderRadius: '6px',
                                border: 'none',
                                background: isSelected ? '#3b82f6' : 'transparent',
                                color: isSelected ? '#fff' : (isDisabled ? '#cbd5e1' : '#475569'),
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: isSelected || isToday ? 600 : 400,
                                position: 'relative'
                              }}
                            >
                              {day}
                              {isToday && !isSelected && <span style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: '#3b82f6', borderRadius: '50%' }} />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="time-picker-widget" style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
                      <h5 style={{ margin: '0 0 16px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Select Time Slot</h5>
                      
                      <div className="time-slots-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Morning</span>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                            {timeSlots.morning.map(time => {
                              const { valid, reason } = isTimeSlotValid(time);
                              // Find ALL booked slots for this time and service
                              const matchingSlots = bookedSlots.filter(slot => {
                                const sameService = slot.serviceId === rescheduleTarget.serviceId || slot.serviceName === rescheduleTarget.serviceName;
                                const sameTime = slot.time === time;
                                return sameService && sameTime;
                              });
                              // Get unique booking types to avoid duplicate dots
                              const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];
                              
                              return (
                                <button
                                  key={time}
                                  type="button"
                                  disabled={!valid}
                                  onClick={() => setSelectedRescheduleTime(time)}
                                  style={{
                                    padding: '8px 2px 16px 2px',
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    borderColor: selectedRescheduleTime === time ? '#29499d' : (valid ? '#e2e8f0' : '#f1f5f9'),
                                    background: selectedRescheduleTime === time ? '#29499d' : (valid ? '#fff' : '#f8fafc'),
                                    color: selectedRescheduleTime === time ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                                    fontSize: '11px',
                                    fontWeight: selectedRescheduleTime === time ? 700 : 500,
                                    cursor: valid ? 'pointer' : 'not-allowed',
                                    position: 'relative'
                                  }}
                                >
                                  {time}
                                  {uniqueBookingTypes.length > 0 && (
                                    <div style={{ 
                                      position: 'absolute', 
                                      bottom: '4px', 
                                      left: '50%', 
                                      transform: 'translateX(-50%)', 
                                      display: 'flex', 
                                      gap: '4px',
                                      alignItems: 'center'
                                    }}>
                                      {uniqueBookingTypes.map((bookingType, idx) => (
                                        <div 
                                          key={idx}
                                          style={{ 
                                            width: '8px', 
                                            height: '8px', 
                                            backgroundColor: bookingType === "WALK_IN" ? '#fdebb2' : '#19458c', 
                                            borderRadius: '50%' 
                                          }} 
                                        />
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Afternoon</span>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                            {timeSlots.afternoon.map(time => {
                              const { valid, reason } = isTimeSlotValid(time);
                              // Find ALL booked slots for this time and service
                              const matchingSlots = bookedSlots.filter(slot => {
                                const sameService = slot.serviceId === rescheduleTarget.serviceId || slot.serviceName === rescheduleTarget.serviceName;
                                const sameTime = slot.time === time;
                                return sameService && sameTime;
                              });
                              // Get unique booking types to avoid duplicate dots
                              const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];
                              
                              return (
                                <button
                                  key={time}
                                  type="button"
                                  disabled={!valid}
                                  onClick={() => setSelectedRescheduleTime(time)}
                                  style={{
                                    padding: '8px 2px 16px 2px',
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    borderColor: selectedRescheduleTime === time ? '#29499d' : (valid ? '#e2e8f0' : '#f1f5f9'),
                                    background: selectedRescheduleTime === time ? '#29499d' : (valid ? '#fff' : '#f8fafc'),
                                    color: selectedRescheduleTime === time ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                                    fontSize: '11px',
                                    fontWeight: selectedRescheduleTime === time ? 700 : 500,
                                    cursor: valid ? 'pointer' : 'not-allowed',
                                    position: 'relative'
                                  }}
                                >
                                  {time}
                                  {uniqueBookingTypes.length > 0 && (
                                    <div style={{ 
                                      position: 'absolute', 
                                      bottom: '4px', 
                                      left: '50%', 
                                      transform: 'translateX(-50%)', 
                                      display: 'flex', 
                                      gap: '4px',
                                      alignItems: 'center'
                                    }}>
                                      {uniqueBookingTypes.map((bookingType, idx) => (
                                        <div 
                                          key={idx}
                                          style={{ 
                                            width: '8px', 
                                            height: '8px', 
                                            backgroundColor: bookingType === "WALK_IN" ? '#fdebb2' : '#19458c', 
                                            borderRadius: '50%' 
                                          }} 
                                        />
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '20px 32px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0 }}>
              <button className="ma-outline-btn" onClick={() => setRescheduleTarget(null)}>Cancel</button>
              <button 
                className="ma-primary-btn" 
                onClick={handleReschedule} 
                disabled={!selectedRescheduleTime || isSubmittingReschedule}
                style={{ minWidth: '140px' }}
              >
                {isSubmittingReschedule ? 'Rescheduling...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CANCEL MODAL */}
      <CancelAppointmentModal 
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        appointment={cancelTarget}
        theme="staff"
        isSubmitting={isCancelling}
      />
    </div>
  );
};

export default ReviewAppointments;
